import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import history from '../../../../core/history';
import {
  CDP_SHARE,
  CDP_CLICK_DIRECTION_ABOVE_THE_FOLD,
  CDP_CLICK_CALL_CLINIC,
} from '../../../../core/analytics/events';
import { fontFamily, fontSize } from '../../../../constants/text';
import { colors } from '../../../../constants/colors';
import { analyticsTrackEvent } from '../../../../core/analytics';
import { getGoogleDirectionsUrl, isBookable, isSolvPartner } from '../../../../core/util/location';
import { CDP_CLICK_TO_CALL_MODAL } from '../../Book/components/ClickToCall';
import { getSmsSeparator } from '../../../../core/util/device';
import { createLocationUrlFromLocation } from '../../../../core/url/location';
import { isEmpty } from '../../../../core/util/empty';
import SaveForLater from './SaveForLater';
import { ButtonVariations } from '../../../SolvPatternLibrary/Button';
import LeftChevron from '@material-ui/icons/ChevronLeft';
import Button from '@material-ui/core/Button';

export const ActionImplementations = {
  BOOKING: 'booking',
  MOBILE: 'mobile',
  DESKTOP: 'desktop',
};

const Root = styled.div`
  display: flex;
  justify-content: left;
`;

const MobileRoot = styled(Root)`
  justify-content: space-between;
`;

const ActionRoot = styled(Root)`
  justify-content: flex-end;
`;

const ActionContainer = styled.a`
  border: ${colors.borderLine} 1px solid;
  border-radius: 5px;
  padding: 8px 10px 10px;
  margin-right: 10px;
  cursor: pointer;
  white-space: nowrap;

  ${({ primary }) =>
    primary &&
    css`
      border-color: ${colors.crunchBerry};
      background-color: ${colors.crunchBerry};
    `}
`;

const MobileActionContainer = styled(ActionContainer)`
  border: none;
  padding: 10px 3px 0;

  &:last-child {
    padding: 10px 3px 0 13px;
  }
`;

const ActionName = styled.span`
  -webkit-font-smoothing: antialiased;
  font-family: ${fontFamily.book};
  font-size: ${fontSize.mediumSmall};
  color: ${colors.squid};
  display: inline-block;
  vertical-align: middle;
  padding: 0 0 0 10px;

  ${({ primary }) =>
    primary &&
    css`
      color: ${colors.white};
    `}
`;

const ActionIcon = styled.img`
  display: inline-block;
  vertical-align: middle;
  height: 16px;
  width: 16px;
`;

const ChevronLeft = styled(LeftChevron)`
  color: ${colors.crunchBerry};

  cursor: pointer;
  display: flex;
  vertical-align: middle;
`;

const GoBackButton = styled(Button)`
  grid-area: back;
  color: ${colors.crunchBerry};
  padding-bottom: 4px;
  text-transform: unset;
  margin-left: -20px;
  padding-top: 12px;
`;

const SHARE = 'Share';
const DIRECTIONS = 'Directions';
const CALL_FOR_VISIT = 'Call for Visit';

const shareAction = {
  name: SHARE,
  icon: '/images/icons/Icon-Share.svg',
  eventName: CDP_SHARE,
  alt: 'Share {displayNamePrimary} with a friend',
};
const directionsAction = {
  name: DIRECTIONS,
  icon: '/images/icons/Icon-Directions.svg',
  eventName: CDP_CLICK_DIRECTION_ABOVE_THE_FOLD,
  alt: 'Directions to {displayNamePrimary}',
};
const nonBookableAction = {
  name: CALL_FOR_VISIT,
  icon: '/images/icons/Icon-Phone.svg',
  primary: true,
  eventName: CDP_CLICK_CALL_CLINIC,
  alt: 'Call Clinic',
};

export default class Actions extends PureComponent {
  static propTypes = {
    location: PropTypes.object.isRequired,
    accountSummary: PropTypes.object,
    implementation: PropTypes.oneOf([
      ActionImplementations.MOBILE,
      ActionImplementations.DESKTOP,
      ActionImplementations.BOOKING,
    ]),
    showModal: PropTypes.func.isRequired,
    className: PropTypes.string,
  };

  static defaultProps = {
    implementation: ActionImplementations.DESKTOP,
  };

  constructor(props) {
    super(props);
    const { location, implementation } = props;
    this.locationUrl = createLocationUrlFromLocation(location);
    this.actions = [];

    const preparedMessage =
      'Check out this healthcare provider I found on Solv: ' +
      `http://solv.me${this.locationUrl}. You can easily book online ` +
      'for today or tomorrow.';

    const shareUrlMobile = `sms:${getSmsSeparator()}body=${preparedMessage}`;

    const shareUrlDesktop = `mailto:?subject=${
      location.displayNamePrimary
    } on Solv&body=${preparedMessage}`;

    this.shareUrl =
      implementation === ActionImplementations.DESKTOP ? shareUrlDesktop : shareUrlMobile;

    switch (implementation) {
      case ActionImplementations.DESKTOP:
        this.actions = [shareAction, directionsAction];
        if (!isBookable(location)) {
          this.actions.unshift(nonBookableAction);
        }
        break;
      case ActionImplementations.MOBILE:
        this.actions = [shareAction, directionsAction];
        break;
      case ActionImplementations.BOOKING:
        this.actions = [directionsAction];
        break;
      default:
        break;
    }
  }

  state = {
    ShareModal: null,
    shareModalOpen: false,
  };

  onShareClick = () =>
    this.props.implementation === ActionImplementations.DESKTOP
      ? this.handleOpenShareModal()
      : this.openMailApp();

  onClick = action => {
    const {
      location: { id, uclId },
    } = this.props;
    const device =
      this.props.implementation === ActionImplementations.DESKTOP ? 'desktop' : 'mobile';
    analyticsTrackEvent(action.eventName, { device, locationId: id, uclId });
    this.actionOnClick[action.name]();
  };

  getActionAltName = action =>
    action.alt.replace('{displayNamePrimary}', this.props.location.displayNamePrimary);

  handleOpenShareModal = () => {
    const { ShareModal } = this.state;

    if (isEmpty(ShareModal)) {
      import(/* webpackChunkName: 'shareModal', webpackPrefetch: true */ './ShareModal').then(
        module => {
          this.setState({ ShareModal: module.default, shareModalOpen: true });
        }
      );
    } else {
      this.setState({ shareModalOpen: true });
    }
  };

  handleCloseShareModal = () => {
    this.setState({ shareModalOpen: false });
  };

  shouldShowBackArrow = () => history.length > 2;

  handleGoBack = e => {
    e && e.preventDefault();
    history.goBack();
  };

  openMailApp = () => window.open(this.shareUrl, '_self');

  actionOnClick = {
    /* eslint-disable no-alert */
    [DIRECTIONS]: () => window.open(getGoogleDirectionsUrl(this.props.location)),
    [CALL_FOR_VISIT]: () => this.props.showModal(CDP_CLICK_TO_CALL_MODAL),
    [SHARE]: this.onShareClick,
  };

  render = () => {
    const {
      props: { className, implementation, location },
      actions,
    } = this;
    const { ShareModal, shareModalOpen } = this.state;

    switch (implementation) {
      default:
      case ActionImplementations.DESKTOP:
        return (
          <Root className={className}>
            {ShareModal && (
              <ShareModal
                open={shareModalOpen}
                onClose={this.handleCloseShareModal}
                locationUrl={this.locationUrl}
                openMailApp={this.openMailApp}
                location={location}
              />
            )}
            {actions.map(action => (
              <ActionContainer
                key={action.name}
                primary={action.primary}
                onClick={() => this.onClick(action)}
              >
                <ActionIcon src={action.icon} alt={this.getActionAltName(action)} />
                <ActionName primary={action.primary}>{action.name}</ActionName>
              </ActionContainer>
            ))}
            {isSolvPartner(location) && <SaveForLater location={location} showText />}
          </Root>
        );
      case ActionImplementations.MOBILE:
        return (
          <MobileRoot>
            {this.shouldShowBackArrow() ? (
              <GoBackButton variation={ButtonVariations.link} onClick={this.handleGoBack}>
                <ChevronLeft /> Back
              </GoBackButton>
            ) : (
              <div />
            )}
            <ActionRoot>
              {isSolvPartner(location) && <SaveForLater location={location} />}
              {actions.map(action => (
                <MobileActionContainer key={action.name} onClick={() => this.onClick(action)}>
                  <ActionIcon src={action.icon} alt={this.getActionAltName(action)} />
                </MobileActionContainer>
              ))}
            </ActionRoot>
          </MobileRoot>
        );
      case ActionImplementations.BOOKING:
        return (
          <Root className={className}>
            {actions.map(action => (
              <ActionContainer
                key={action.name}
                primary={action.primary}
                onClick={() => this.onClick(action)}
              >
                <ActionIcon src={action.icon} alt={this.getActionAltName(action)} />
                <ActionName primary={action.primary}>{action.name}</ActionName>
              </ActionContainer>
            ))}
          </Root>
        );
    }
  };
}
